/**
 * useAIGreeting Hook
 * 
 * Manages the "waiting for AI to speak first" state.
 * Prevents user from speaking before AI greets them.
 */

import { useState, useEffect, useRef } from 'react';
import { logger } from '../utils/logger';

interface UseAIGreetingReturn {
  waitingForAIGreeting: boolean;
  setWaitingForAIGreeting: (waiting: boolean) => void;
  isFirstGreeting: boolean; // NEW: True during the entire first greeting
  callbackCalledCount: number; // DEBUG: How many times callback was called
}

export function useAIGreeting(
  isAISpeaking: boolean,
  aiResponseInProgress: string | null,
  isPlaying: boolean, // Audio playback state from AudioManager
  onAIFinishedGreeting?: () => void,
  onEnableMicrophoneAfterWarmup?: () => void // Called when greeting finishes (enables microphone)
): UseAIGreetingReturn {
  const [waitingForAIGreeting, setWaitingForAIGreeting] = useState(false);
  const [isFirstGreeting, setIsFirstGreeting] = useState(false);
  const [greetingStarted, setGreetingStarted] = useState(false);
  const [callbackCalledCount, setCallbackCalledCount] = useState(0);

  // Store callbacks in refs to avoid stale closure issues
  const onAIFinishedGreetingRef = useRef(onAIFinishedGreeting);
  const onEnableMicrophoneRef = useRef(onEnableMicrophoneAfterWarmup);
  
  useEffect(() => {
    onAIFinishedGreetingRef.current = onAIFinishedGreeting;
    onEnableMicrophoneRef.current = onEnableMicrophoneAfterWarmup;
  }, [onAIFinishedGreeting, onEnableMicrophoneAfterWarmup]);

  // Remove spam logging

  /**
   * Effect: Detect when AI greeting starts
   */
  const hasActivatedRef = useRef(false);
  
  const prevIsPlayingRef = useRef(false);
  const greetingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  
  useEffect(() => {
    // If we're waiting for greeting and AI starts speaking
    if (waitingForAIGreeting && !greetingStarted && (isAISpeaking || aiResponseInProgress)) {
      logger.debug('[🎤 GREETING] Avatar started greeting - mic stays paused');
      setGreetingStarted(true);
      setIsFirstGreeting(true);
      hasActivatedRef.current = false; // Reset activation flag for new greeting
      prevIsPlayingRef.current = false; // Reset playing state tracker

      // Safety: if greeting never completes (audio never plays or never stops),
      // force-unlock after 15s so the session doesn't get permanently stuck.
      if (greetingTimeoutRef.current) clearTimeout(greetingTimeoutRef.current);
      greetingTimeoutRef.current = setTimeout(() => {
        if (!hasActivatedRef.current) {
          logger.debug('[🎤 GREETING] ⚠️ Greeting never completed after 15s - force-unlocking');
          hasActivatedRef.current = true;
          Promise.resolve(onEnableMicrophoneRef.current?.())
            .catch(() => {})
            .finally(() => {
              setIsFirstGreeting(false);
              setWaitingForAIGreeting(false);
              setGreetingStarted(false);
              onAIFinishedGreetingRef.current?.();
            });
        }
      }, 15000);
    }
  }, [waitingForAIGreeting, greetingStarted, isAISpeaking, aiResponseInProgress]);

  /**
   * Effect: Detect when AI greeting audio finishes
   * Track isPlaying transitions: only activate when goes from true → false
   */
  useEffect(() => {
    // Detect transition from playing to not playing
    const wasPlaying = prevIsPlayingRef.current;
    const isNowPlaying = isPlaying;
    
    // Update ref for next iteration
    prevIsPlayingRef.current = isNowPlaying;
    
    // Only proceed if we're in greeting mode
    if (!isFirstGreeting || !greetingStarted) {
      return;
    }
    
    // Activate mic when audio STOPS (true → false transition)
    if (wasPlaying && !isNowPlaying && !hasActivatedRef.current) {
      logger.debug('[🎤 GREETING] ✅ Audio finished (transition detected) - enabling microphone');
      hasActivatedRef.current = true;
      setCallbackCalledCount(prev => prev + 1);

      // Clear the overall greeting safety timeout since we're handling it now
      if (greetingTimeoutRef.current) {
        clearTimeout(greetingTimeoutRef.current);
        greetingTimeoutRef.current = null;
      }

      // Enable microphone, then unlock UI. If the mic callback fails or
      // takes too long, unlock anyway so the session doesn't get stuck.
      const unlockUI = () => {
        logger.debug('[🎤 GREETING] ✅ Unlocking UI for user input');
        setIsFirstGreeting(false);
        setWaitingForAIGreeting(false);
        setGreetingStarted(false);
        onAIFinishedGreetingRef.current?.();
      };

      // Safety timeout: if mic activation hangs, force-unlock after 5s
      const safetyTimeout = setTimeout(() => {
        logger.debug('[🎤 GREETING] ⚠️ Mic activation timed out (5s) - force-unlocking UI');
        unlockUI();
      }, 5000);

      Promise.resolve(onEnableMicrophoneRef.current?.())
        .then(() => {
          clearTimeout(safetyTimeout);
          unlockUI();
        })
        .catch((err) => {
          clearTimeout(safetyTimeout);
          console.error('[🎤 GREETING] ❌ Mic activation failed - unlocking UI anyway:', err);
          unlockUI();
        });
    }
  }, [isFirstGreeting, greetingStarted, isPlaying]);

  // Wrap setWaitingForAIGreeting to add logging
  const setWaitingForAIGreetingWithLog = (waiting: boolean) => {
    logger.debug('[🎤 GREETING] 🔄 setWaitingForAIGreeting called with:', waiting);
    setWaitingForAIGreeting(waiting);
  };

  return {
    waitingForAIGreeting,
    setWaitingForAIGreeting: setWaitingForAIGreetingWithLog,
    isFirstGreeting, // NEW: Indicates mic should stay paused during first greeting
    callbackCalledCount // DEBUG: Counter to verify callback execution
  };
}

